import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type { MRT_PaginationState } from "material-react-table";

interface UsePageUrlSyncOptions {
  /** Fixed page size */
  pageSize: number;

  /** URL parameter name for page (default: 'page') */
  pageParamName?: string;
}

interface UsePageUrlSyncReturn {
  /** Current pagination state in MRT format */
  pagination: MRT_PaginationState;

  /** Set pagination (MRT format) — uses history.push for back/forward support */
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;

  /** Reset page to 0 without creating a history entry (for filter/search/sort changes) */
  resetPage: () => void;
}

/**
 * Hook to synchronize pagination state with URL parameters.
 *
 * - URL uses 1-based page numbers (?page=3), internal state uses 0-based pageIndex
 * - Page 1 (pageIndex 0) omits the param for cleaner URLs
 * - User page changes use history.push (back/forward works)
 * - Filter/sort resets use history.replace (no history noise)
 */
export function usePageUrlSync(
  options: UsePageUrlSyncOptions,
): UsePageUrlSyncReturn {
  const { pageSize, pageParamName = "page" } = options;

  const history = useHistory();
  const location = useLocation();

  const isInternalUpdate = useRef(false);
  const lastUrlRef = useRef(location.search);

  // Read initial page from URL (1-based → 0-based)
  const [pagination, setPaginationState] = useState<MRT_PaginationState>(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get(pageParamName);
    let pageIndex = 0;
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed) && parsed >= 1) pageIndex = parsed - 1;
    }
    return { pageIndex, pageSize };
  });

  // Ref to distinguish user-initiated vs reset page changes
  const isResetRef = useRef(false);

  // Update URL when pageIndex changes
  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);

    if (pagination.pageIndex > 0) {
      currentParams.set(pageParamName, String(pagination.pageIndex + 1));
    } else {
      currentParams.delete(pageParamName);
    }

    const newSearch = currentParams.toString();
    const searchWithPrefix = newSearch ? `?${newSearch}` : "";

    if (window.location.search !== searchWithPrefix) {
      isInternalUpdate.current = true;
      lastUrlRef.current = searchWithPrefix;

      // Use replace for resets (no history noise), push for user navigation
      if (isResetRef.current) {
        history.replace({ search: searchWithPrefix });
      } else {
        history.push({ search: searchWithPrefix });
      }

      requestAnimationFrame(() => {
        isInternalUpdate.current = false;
      });
    }

    isResetRef.current = false;
  }, [pagination.pageIndex, pageParamName, history]);

  // Listen for external URL changes (browser back/forward)
  useEffect(() => {
    if (isInternalUpdate.current || location.search === lastUrlRef.current) {
      return;
    }

    lastUrlRef.current = location.search;
    const params = new URLSearchParams(location.search);
    const pageParam = params.get(pageParamName);
    const newPageIndex = pageParam
      ? Math.max(0, parseInt(pageParam, 10) - 1)
      : 0;

    if (!isNaN(newPageIndex)) {
      setPaginationState((prev) =>
        prev.pageIndex !== newPageIndex
          ? { ...prev, pageIndex: newPageIndex }
          : prev,
      );
    }
  }, [location.search, pageParamName]);

  // User-initiated pagination change (creates history entry)
  const setPagination: React.Dispatch<
    React.SetStateAction<MRT_PaginationState>
  > = useCallback((updater) => {
    setPaginationState(updater);
  }, []);

  // Reset page without creating history entry (for filter/search/sort changes)
  const resetPage = useCallback(() => {
    isResetRef.current = true;
    setPaginationState((prev) =>
      prev.pageIndex !== 0 ? { ...prev, pageIndex: 0 } : prev,
    );
  }, []);

  return { pagination, setPagination, resetPage };
}
